import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TextareaAutosize from "react-textarea-autosize";
import Header from "../components/Header.jsx";
import SocialActivityItem from "../components/SocialActivityItem";
import SocialActivityForm from "../components/SocialActivityForm";
import Modal from "../components/Modal";
import Loader from "../components/Loader";
import { fetchSocialActivities, updateSocialActivity } from "../services/socialActivityService";
import "../styles/pages/social-activities_page.css";
import "../styles/components/form.css";

const SocialActivitiesPage = () => {
    const [project, setProject] = useState({
        id: "",
        title: "",
        subtitle: "",
        points: []
    });
    const [showForm, setShowForm] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [deleteIndex, setDeleteIndex] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                const data = await fetchSocialActivities();
                if (data.length > 0) {
                    setProject({
                        id: data[0].id,
                        title: data[0].title,
                        subtitle: data[0].subtitle,
                        points: data[0].points
                    });
                }
            } catch (error) {
                if (error.response?.status === 403) {
                    toast.error("Время сессии истекло, пожалуйста войдите заново", {
                        position: "bottom-left",
                        autoClose: 3000
                    });
                } else {
                    toast.error("Ошибка загрузки данных", {
                        position: "bottom-left",
                        autoClose: 3000
                    });
                }
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, []);

    const handleSaveActivity = (text) => {
        const updatedPoints = [...project.points];

        if (editingIndex !== null) {
            updatedPoints[editingIndex] = text;
        } else {
            updatedPoints.push(text);
        }

        setProject(prev => ({
            ...prev,
            points: updatedPoints
        }));

        setShowForm(false);
        setEditingIndex(null);
    };

    const handleConfirmDelete = () => {
        const updatedPoints = project.points.filter((_, i) => i !== deleteIndex);
        setProject(prev => ({
            ...prev,
            points: updatedPoints
        }));
        setDeleteIndex(null);
    };

    const handleSave = async () => {
        setIsSubmitting(true);
        try {
            const updatedProject = {
                ...project,
                title: project.title.trim(),
                subtitle: project.subtitle.trim(),
                points: project.points.filter(p => p.trim() !== "")
            };

            await updateSocialActivity(project.id, updatedProject);

            toast.success("Изменения успешно сохранены", {
                position: "bottom-left",
                autoClose: 3000
            });
        } catch (error) {
            if (error.response?.status === 403) {
                toast.error("Время сессии истекло, пожалуйста войдите заново", {
                    position: "bottom-left",
                    autoClose: 3000
                });
            } else {
                toast.error("Ошибка сохранения изменений", {
                    position: "bottom-left",
                    autoClose: 3000
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Все поля необязательные - кнопка всегда активна
    const isFormValid = true;

    return (
        <div className="social-activities-page">
            <Header />
            <ToastContainer />

            {isLoading ? (
                <div className="loader-wrapper">
                    <Loader />
                    <p className="loading-text">Загрузка данных...</p>
                </div>
            ) : (
                <div className="social-activities-page_content">
                    <h2 className="section-title">Информация социальной деятельности</h2>

                    <div className="form-wrapper">
                        <div className="form-group">
                            <label className="field-label">Основной заголовок</label>
                            <TextareaAutosize
                                className="textarea-input white-field"
                                value={project.title}
                                onChange={(e) => setProject(prev => ({
                                    ...prev,
                                    title: e.target.value
                                }))}
                                placeholder="Введите заголовок проекта"
                            />
                        </div>

                        <div className="form-group">
                            <label className="field-label">Подзаголовок</label>
                            <TextareaAutosize
                                className="textarea-input white-field"
                                value={project.subtitle}
                                onChange={(e) => setProject(prev => ({
                                    ...prev,
                                    subtitle: e.target.value
                                }))}
                                placeholder="Введите подзаголовок проекта"
                            />
                        </div>

                        <div className="activities-section">
                            <label className="field-label">Список пунктов</label>
                            <div className="activities-list">
                                {project.points.map((point, index) => (
                                    <SocialActivityItem
                                        key={index}
                                        index={index}
                                        activity={point}
                                        onEdit={() => {
                                            setEditingIndex(index);
                                            setShowForm(true);
                                        }}
                                        onDelete={() => setDeleteIndex(index)}
                                    />
                                ))}
                                <button
                                    type="button"
                                    className="add-to-list-button"
                                    onClick={() => {
                                        setEditingIndex(null);
                                        setShowForm(true);
                                    }}
                                >
                                    + Добавить пункт
                                </button>
                            </div>
                        </div>

                        <button
                            className="no-form-save-button"
                            onClick={handleSave}
                            disabled={!isFormValid || isSubmitting}
                        >
                            Сохранить изменения
                        </button>

                        {isSubmitting && (
                            <div className="loader-overlay">
                                <Loader />
                                <p>Сохранение изменений...</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showForm && (
                <SocialActivityForm
                    onClose={() => {
                        setShowForm(false);
                        setEditingIndex(null);
                    }}
                    onSave={handleSaveActivity}
                    initialText={editingIndex !== null ? project.points[editingIndex] : ""}
                    mode={editingIndex !== null ? "edit" : "create"}
                />
            )}

            {deleteIndex !== null && (
                <Modal
                    isOpen={true}
                    message="Вы уверены что хотите удалить пункт?"
                    itemTitle={project.points[deleteIndex]}
                    onCancel={() => setDeleteIndex(null)}
                    onConfirm={handleConfirmDelete}
                />
            )}
        </div>
    );
};

export default SocialActivitiesPage;